/** @jsx jsx */
import { jsx } from '@emotion/core';
import ReactDOMServer from 'react-dom/server';
import defaultsDeep from 'lodash/defaultsDeep';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import Helmet from 'react-helmet';
import axios from 'axios';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import { secondsToMilliseconds } from '../../../utils/number';
import { ANIMATION_LENGTH } from '../../../utils/animations';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { FIRST_NAME_INPUT, EMAIL_INPUT, API } from '../../../utils/constants';
import { setDataLayerObj } from '../../../utils/analytics';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import * as styles from './index.styles';

const defaultData = {
  DevKey: { value: '' },
  ORGUID: { value: '' },
  QueueName: { value: '' },
  QueueID: { value: '' },
  ClientID: { value: '' },
  ClientSecret: { value: '' },
  IdleTime: { value: '' },
  MaxUsersOnQueue: { value: '' },
  MaxWaitingTimeToContactAgent: { value: '' },
  Switch: { value: '' },
  WaitingTimeToContactAgentCopy: { value: '' },
};

// GENESYS Purecloud webchat docs https://developer.mypurecloud.com/developer-tools/#/webchat
export class Webchat extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);
  state = {
    displayChat: false,
  };

  componentDidMount() {
    const chatIdleTimeOut = this.getMillisecondsFromString(
      this.fields.IdleTime.value,
    );

    window._genesys = window._genesys || {};
    window._genesys.widgets = this.getWidgetConfigs();

    setTimeout(() => {
      this.handleAPI();
    }, chatIdleTimeOut);
  }

  getChatTriggerElement = () =>
    ReactDOMServer.renderToStaticMarkup(
      <div className="chat-trigger" css={styles.chatTriggerWrapper}>
        <span css={styles.chatTriggerText}>{this.props.t('NeedHelp')}</span>
      </div>,
    );

  validateEmptyField(input) {
    return get(input, '[0].value') ? true : false;
  }

  getWidgetConfigs = () => {
    const { t } = this.props;
    const { DevKey, ORGUID, QueueName } = this.fields;

    return {
      extensions: {}, // https://docs.genesys.com/Documentation/GWC/Current/CXWBusAPI/GWCGWCCXBusExtensions
      main: {
        downloadGoogleFont: false,
        mobileMode: 'auto',
        mobileModeBreakpoint: Breakpoints.md,
        preload: ['webchat'],
        theme: 'light',
      },
      onReady: () => this.handleEvents(),
      webchat: {
        actionsMenu: true,
        emojis: true,
        uploadsEnabled: true,
        transport: {
          type: 'purecloud-v2-sockets',
          dataURL: 'https://api.mypurecloud.com',
          deploymentKey: DevKey.value,
          orgGuid: ORGUID.value,
          interactionData: {
            routing: {
              targetType: 'QUEUE',
              targetAddress: QueueName.value,
              priority: 2,
            },
          },
        },
        autoInvite: {
          enabled: true,
        },
        chatButton: {
          enabled: false,
          effect: 'fade',
          effectDuration: ANIMATION_LENGTH._300MS,
          hideDuringInvite: true,
          openDelay: ANIMATION_LENGTH._1000MS,
          template: this.getChatTriggerElement(),
        },
        form: {
          inputs: [
            {
              id: FIRST_NAME_INPUT,
              label: t('FirstNamePlaceholder'),
              maxlength: '250',
              name: FIRST_NAME_INPUT,
              placeholder: t('RequiredPlaceholder'),
              validate: (_event, _form, input) =>
                this.validateEmptyField(input),
            },
            {
              id: EMAIL_INPUT,
              label: t('EmailPlaceholder'),
              maxlength: '250',
              name: EMAIL_INPUT,
              placeholder: t('OptionalPlaceholder'),
              type: 'email',
            },
          ],
        },
      },
    };
  };

  handleChatDisplay = queueData => {
    const queueResults = get(queueData, 'results');

    const isAgentOnQueue = Boolean(
      get(queueData, 'systemToOrganizationMappings.ON_QUEUE', false),
    );

    const handleQueueCount = queueCount => {
      const isQueueAvailable =
        queueCount <= parseInt(this.fields.MaxUsersOnQueue.value);

      if (isAgentOnQueue && isQueueAvailable) {
        this.setState({ displayChat: true });
      }
    };

    if (Boolean(queueResults)) {
      queueResults.forEach(result => {
        const onWaitingMetric = find(result.data, {
          metric: 'oWaiting',
        });

        if (onWaitingMetric) {
          const queueCount = get(onWaitingMetric, 'stats.count');

          handleQueueCount(queueCount);
        }
      });
    }
  };

  handleAPI = () => {
    const sessionStorageValues =
      typeof window !== 'undefined' ? window.sessionStorage : {};

    const chatAPITriggered = Boolean(
      get(sessionStorageValues, 'chatAPITriggered'),
    );

    if (!chatAPITriggered) {
      const queueURLwithParam = `${API.WEBCHAT}?queueId=${this.fields.QueueID.value}`;

      window.sessionStorage.setItem('chatAPITriggered', true);

      axios
        .get(queueURLwithParam)
        .then(response => {
          if (!isEmpty(response.data)) {
            this.handleChatDisplay(response.data);
          }
        })
        .catch(() => {});
    }
  };

  handleStartedChat = registeredExtension => {
    const {
      MaxWaitingTimeToContactAgent,
      WaitingTimeToContactAgentCopy,
    } = this.fields;
    const maxWaitingTime = this.getMillisecondsFromString(
      MaxWaitingTimeToContactAgent.value,
    );
    const isMaxWaitingTimeValid = maxWaitingTime > 0;

    registeredExtension.subscribe('WebChatService.started', () => {
      setDataLayerObj({ event: 'webChatStarted' });

      if (isMaxWaitingTimeValid) {
        setTimeout(() => {
          registeredExtension
            .command('WebChatService.getAgents')
            .done(response => {
              if (isEmpty(response.agents)) {
                registeredExtension
                  .command('WebChatService.sendMessage', {
                    message: WaitingTimeToContactAgentCopy.value,
                  })
                  .fail(() => {});
              }
            })
            .fail(() => {});
        }, maxWaitingTime);
      }
    });
  };

  handleEvents = () => {
    const extensionsReady = get(window, 'CXBus.registerPlugin');

    // https://docs.genesys.com/Documentation/GWC/latest/WidgetsAPI/WebChatServiceCommands
    // https://all.docs.genesys.com/WID/Current/SDK/WebChatService-combined#events
    const registeredExtension = window.CXBus.registerPlugin(
      'chatServiceExtension',
    );

    if (!extensionsReady) return;

    registeredExtension.subscribe('Engage.ready', () => {
      setDataLayerObj({ event: 'webChatIntroBox' });
    });

    this.handleStartedChat(registeredExtension);
  };

  getMillisecondsFromString = string => {
    const getInt = parseInt(string);

    return secondsToMilliseconds(getInt);
  };

  safeRender() {
    const isEditing = get(this.props, 'sitecoreContext.pageEditing', false);

    if (!this.fields.Switch.value) {
      return null;
    }

    if (isEditing) {
      return <h2>Web Chat</h2>;
    }

    return (
      this.state.displayChat && (
        <Helmet>
          <script
            id="genesys-cx-widget-script"
            src="https://apps.mypurecloud.com/widgets/9.0/widgets.min.js"
          ></script>

          <link
            id="genesys-widgets-styles"
            href="https://apps.mypurecloud.com/widgets/9.0/widgets.min.css"
            rel="stylesheet"
            type="text/css"
          />
        </Helmet>
      )
    );
  }
}

Webchat.displayName = 'Webchat';

Webchat.defaultProps = {
  t: key => key,
  fields: defaultData,
};

const stringValueType = PropTypes.shape({
  value: PropTypes.string,
});

Webchat.propTypes = {
  t: PropTypes.func,
  fields: PropTypes.shape({
    ClientID: stringValueType,
    ClientSecret: stringValueType,
    DevKey: stringValueType,
    IdleTime: stringValueType,
    MaxUsersOnQueue: stringValueType,
    MaxWaitingTimeToContactAgent: stringValueType,
    ORGUID: stringValueType,
    QueueID: stringValueType,
    QueueName: stringValueType,
    WaitingTimeToContactAgentCopy: stringValueType,
  }),
};

export default compose(withNamespaces(), withSitecoreContext())(Webchat);
